import Permission from "permissions/permission";
import { GeneralDocumentAccess } from "permissions/resources/documents/documents-permissions-resources";
import { LegalEntityAccess } from "permissions/resources/legal-entities/legal-entities-permissions-resources";
import { LegalEntityAccessType } from "permissions/resources/legal-entities/legal-entity-access-types";
import { KnowledgeBaseAccess } from "permissions/resources/knowledge-base/knowledge-base-permissions-resources";
import {
  MarketplaceAccess,
  MarketplaceSearchAccess,
} from "permissions/resources/marketplace/marketplace-permissions-resources";
import { CommunicationsAccess } from "permissions/resources/messaging/messaging-permissions-resources";
import { NotificationsAccess } from "permissions/resources/notifications/notifications-permissions-resources";
import { AccessLevel, Scope } from "permissions/resources/permission-resource-types";
import { UserNetworkingAccess } from "permissions/resources/users/users-permissions-resources";
import {
  ContractsAccess,
  DiscountsAccess,
  FeeSchedulesAccess,
  ProposalsAccess,
  TeamsAccess,
  WorkAgreementsAccess,
} from "permissions/resources/work/work-permissions-resources";

export class SessionPermissions {
  public get permissions(): Permission[] {
    return [...this._permissions];
  }

  public get includesAccessToCommunications(): boolean {
    const permission = this._permissions.find((p) => p.resource instanceof CommunicationsAccess);
    if (!permission) return false;
    const resource = permission.resource as CommunicationsAccess;
    return (
      resource.accessLevel !== undefined &&
      resource.accessLevel !== null &&
      resource.accessLevel >= AccessLevel["Read-Only"]
    );
  }

  /**
   * Whether this session may read the entity-wide notifications feed.
   *
   * Read/write access to one's own notifications is inherent and never appears as a stored grant,
   * so it is not what this asks about (issue #4438). This mirrors the API's gate on
   * `GET /notifications/entity`, which asserts entity-wide scope at read-only, so both read and
   * read/write holders qualify.
   */
  public get includesEntityWideAccessToNotifications(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof NotificationsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as NotificationsAccess;
      return (
        !permission.isDisabled &&
        resource.scope !== undefined &&
        resource.scope !== null &&
        resource.scope >= Scope["Entity-Wide"]
      );
    });
  }

  // Mirrors the API's upload gate: Document.CreateAsync asserts GeneralDocumentAccess at
  // Need-to-Know scope and Read/Write access, so any scope qualifies and only the access
  // level is decisive. Kept in step with that assertion so the UI never offers an upload
  // the server will reject.
  public get includesReadWriteAccessToGeneralDocuments(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof GeneralDocumentAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as GeneralDocumentAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read/Write"]
      );
    });
  }

  public get includesSigningAuthority(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof LegalEntityAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as LegalEntityAccess;
      return resource.accessType === LegalEntityAccessType["Signing Authority"];
    });
  }

  public get includesAccessToAdministration(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof LegalEntityAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as LegalEntityAccess;
      return (
        (resource.accessType === LegalEntityAccessType["Member Information"] &&
          resource.scope >= Scope["Entity-Wide"] &&
          resource.accessLevel >= AccessLevel["Read/Write"]) ||
        (resource.accessType === LegalEntityAccessType["Member Permissions"] &&
          resource.scope >= Scope["Entity-Wide"]) ||
        (resource.accessType === LegalEntityAccessType["Member Invitations"] && resource.scope >= Scope["Entity-Wide"])
      );
    });
  }

  /**
   * Whether this session may read the organization's roster.
   *
   * Narrower than {@link includesAccessToAdministration}, and deliberately so: that one is true
   * for Member Permissions or Member Invitations as well, and the roster endpoint accepts
   * neither. `GET legal-entities/{id}/members` asserts Member Information at Entity-Wide and
   * Read-Only (`LegalEntityQueryService.FindEntityMembersAsync`), so anything that calls it has
   * to ask this question rather than the umbrella one.
   */
  public get includesReadAccessToMemberInformation(): boolean {
    return this.hasLegalEntityAccess(LegalEntityAccessType["Member Information"]);
  }

  /**
   * Whether this session may read the organization's pending invitations.
   *
   * `GET legal-entities/{id}/user-entity-member-invitations` asserts Member Invitations at
   * Entity-Wide and Read-Only. A page that asked on the strength of
   * {@link includesAccessToAdministration} spent a 403 and a console error on a reader who was
   * entitled to be there — they simply administer something else.
   */
  public get includesReadAccessToMemberInvitations(): boolean {
    return this.hasLegalEntityAccess(LegalEntityAccessType["Member Invitations"]);
  }

  public get includesAccessToWork(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof WorkAgreementsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as WorkAgreementsAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  public get includesAccessToProposals(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof ProposalsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as ProposalsAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  /**
   * Whether this session may ORIGINATE a proposal, which is a strictly higher bar than reading one.
   *
   * `ProposalCommandsController.CreateProposalAsync` asserts ProposalsAccess at **Entity-Wide** scope
   * and Read/Write as its first statement, while reading asserts Need-to-Know at Read-Only. Creating
   * a proposal commits the firm to an offer, so it is an entity-level act by design.
   *
   * {@link includesAccessToProposals} cannot stand in for this: it ignores scope entirely, so a member
   * holding Read/Write at Need-to-Know passes it, reaches the draft editor, fills in a whole proposal
   * and only learns at Save that the server was never going to accept it.
   */
  public get includesEntityWideReadWriteAccessToProposals(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof ProposalsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as ProposalsAccess;
      return (
        !permission.isDisabled &&
        resource.scope !== undefined &&
        resource.scope !== null &&
        resource.scope >= Scope["Entity-Wide"] &&
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read/Write"]
      );
    });
  }

  /**
   * Whether this session may read its own organization's firm roll-outs.
   *
   * `VendorRolloutBoardService.AssertMayReadAsync` asserts ProposalsAccess at **Entity-Wide** scope
   * and Read-Only when the caller asks about their own organization, so both read and read/write
   * holders qualify — unlike {@link includesEntityWideReadWriteAccessToProposals}, which is the
   * originating bar.
   *
   * Scope is what is decisive here and {@link includesAccessToProposals} ignores it: a member
   * holding Need-to-Know sees only the proposals they are on, whereas the board is every firm the
   * organization has invited. That is a supplier list, which is an entity-level thing to know.
   */
  public get includesEntityWideAccessToProposals(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof ProposalsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as ProposalsAccess;
      return (
        !permission.isDisabled &&
        resource.scope !== undefined &&
        resource.scope !== null &&
        resource.scope >= Scope["Entity-Wide"] &&
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  /**
   * Whether this session may set a discount that applies across the organization.
   *
   * `ClientProposalTemplatesController.UpdateClientProposalTemplate` asserts DiscountsAccess at
   * **Entity-Wide** scope and Read/Write, separately from the proposals assertion beside it. A
   * discount set on the proposal template configuration is applied to every firm the organization
   * onboards, so somebody who may build proposals but not price them does not acquire that by
   * coming through this door.
   */
  public get includesEntityWideReadWriteAccessToDiscounts(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof DiscountsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as DiscountsAccess;
      return (
        !permission.isDisabled &&
        resource.scope !== undefined &&
        resource.scope !== null &&
        resource.scope >= Scope["Entity-Wide"] &&
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read/Write"]
      );
    });
  }

  public get includesAccessToTeams(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof TeamsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as TeamsAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  /**
   * Whether this session may CREATE a team template, which is a strictly higher bar than reading one.
   *
   * `TeamTemplateAdministrationService.AssertCanCreateTeamTemplateAsync` asserts TeamsAccess at
   * **Entity-Wide** scope and Read/Write, while listing asserts Need-to-Know at Read-Only. A team
   * template is the organization's reusable staffing, so authoring one is an entity-level act by
   * design.
   *
   * {@link includesAccessToTeams} cannot stand in for this: it ignores scope entirely, so a member
   * holding Read/Write at Need-to-Know passes it, reaches the create form, fills in a whole template
   * and only learns at Save that the server was never going to accept it (issue #4948).
   */
  public get includesEntityWideReadWriteAccessToTeams(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof TeamsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as TeamsAccess;
      return (
        !permission.isDisabled &&
        resource.scope !== undefined &&
        resource.scope !== null &&
        resource.scope >= Scope["Entity-Wide"] &&
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read/Write"]
      );
    });
  }

  public get includesAccessToFeeSchedules(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof FeeSchedulesAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as FeeSchedulesAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  /**
   * Whether this session may AUTHOR a fee schedule template, which is a higher bar than listing one.
   *
   * `FeeScheduleTemplate.CreateAsync` asserts FeeSchedulesAccess at Need-to-Know and Read/Write, so
   * unlike proposals and team templates the scope is not decisive here and only the access level is.
   * {@link includesAccessToFeeSchedules} cannot stand in for it: that one passes on Read-Only, so a
   * member who may only read the schedules was offered Create Template and refused at save (#4651).
   */
  public get includesReadWriteAccessToFeeSchedules(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof FeeSchedulesAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as FeeSchedulesAccess;
      return (
        !permission.isDisabled &&
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read/Write"]
      );
    });
  }

  public get includesAccessToContracts(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof ContractsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as ContractsAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  public get includesAccessToKnowledgeBase(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof KnowledgeBaseAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as KnowledgeBaseAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  /**
   * Whether this user may write knowledge-base content for their own organization.
   *
   * Scope as well as access level, because the aggregates ask for both: a basic member holds
   * need-to-know read/write, and reading the access level alone reported them as an author. The
   * browser then offered New article and Edit to people whose every save the server refused.
   */
  public get includesWriteAccessToKnowledgeBase(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof KnowledgeBaseAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as KnowledgeBaseAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read/Write"] &&
        resource.scope !== undefined &&
        resource.scope !== null &&
        resource.scope >= Scope["Entity-Wide"]
      );
    });
  }

  public get includesReadAccessToMarketplace(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof MarketplaceAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as MarketplaceAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  public get includesReadWriteAccessToMarketplace(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof MarketplaceAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as MarketplaceAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read/Write"]
      );
    });
  }

  public get includesAccessToMarketplaceSearch(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof MarketplaceSearchAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as MarketplaceSearchAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  public get includesAccessToNetwork(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof UserNetworkingAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as UserNetworkingAccess;
      return (
        !permission.isDisabled &&
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  constructor(private readonly _permissions: Permission[]) {}

  /** Entity-wide read of one legal-entity resource, which is what these endpoints assert. */
  private hasLegalEntityAccess(accessType: LegalEntityAccessType): boolean {
    return this._permissions.some((permission) => {
      if (!(permission.resource instanceof LegalEntityAccess)) return false;
      const resource = permission.resource;
      return (
        resource.accessType === accessType &&
        resource.scope >= Scope["Entity-Wide"] &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }
}
